/**
 * Busca da malha municipal de uma UF (FB-04): `public/geo/mun/{UF}.{hash}.topo.json`, pelo manifesto gerado
 * (`mun-manifest.ts`). Só `data/` faz fetch (§2.2.2 regra 1): daqui sai o TopoJSON bruto; quem valida e decodifica é
 * `map/MunStore.ts`. O arquivo é imutável (hash de conteúdo no nome): `cache: 'default'`. Falha de transporte tem nova
 * tentativa em 2 s e 6 s; arquivo ausente do manifesto ou JSON quebrado, não.
 */
import { MUN_MESH } from './mun-manifest';

export type MunMeshFailure = 'missing' | 'network' | 'timeout' | 'http' | 'parse' | 'aborted';

export class MunMeshError extends Error {
  constructor(
    readonly uf: string,
    readonly reason: MunMeshFailure,
    readonly detail = '',
  ) {
    super(`malha mun/${uf}: ${reason}${detail ? ` (${detail})` : ''}`);
    this.name = 'MunMeshError';
  }
  get retryable(): boolean {
    return this.reason === 'network' || this.reason === 'timeout' || this.reason === 'http';
  }
}

export const MUN_MESH_RETRY_DELAYS_MS = [2000, 6000] as const;
export const MUN_MESH_TIMEOUT_MS = 20_000;

export interface LoadMunMeshOptions {
  manifest?: Readonly<Record<string, string>>;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  timeoutMs?: number;
}

/** Caminho servido da malha da UF; null se a UF não tem malha gerada. */
export function munMeshPath(uf: string, manifest: Readonly<Record<string, string>> = MUN_MESH): string | null {
  return Object.hasOwn(manifest, uf) ? (manifest[uf] ?? null) : null;
}

async function fetchOnce(uf: string, path: string, o: LoadMunMeshOptions, signal?: AbortSignal): Promise<unknown> {
  const fetchImpl = o.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const ctrl = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    ctrl.abort();
  }, o.timeoutMs ?? MUN_MESH_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    let text: string;
    try {
      const res = await fetchImpl(path, { cache: 'default', redirect: 'error', signal: ctrl.signal });
      if (!res.ok) throw new MunMeshError(uf, 'http', String(res.status));
      text = await res.text();
    } catch (e) {
      if (e instanceof MunMeshError) throw e;
      if (signal?.aborted) throw new MunMeshError(uf, 'aborted');
      throw new MunMeshError(uf, timedOut ? 'timeout' : 'network', (e as Error | null)?.message ?? '');
    }
    try {
      return JSON.parse(text) as unknown;
    } catch (e) {
      throw new MunMeshError(uf, 'parse', (e as Error).message);
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}

/** A malha da UF, bruta. Rejeita só com `MunMeshError`. */
export async function loadMunMesh(uf: string, signal?: AbortSignal, o: LoadMunMeshOptions = {}): Promise<unknown> {
  if (signal?.aborted) throw new MunMeshError(uf, 'aborted');
  const path = munMeshPath(uf, o.manifest);
  if (!path) throw new MunMeshError(uf, 'missing', 'sem arquivo no manifesto');
  const sleep = o.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchOnce(uf, path, o, signal);
    } catch (e) {
      const error = e instanceof MunMeshError ? e : new MunMeshError(uf, 'network', String(e));
      const delay = error.retryable ? MUN_MESH_RETRY_DELAYS_MS[attempt] : undefined;
      if (delay === undefined) throw error;
      await sleep(delay);
      if (signal?.aborted) throw new MunMeshError(uf, 'aborted');
    }
  }
}
